import {
  auditStatusLabel,
  auditStatusTone,
  fetchAuditRuns,
  formatAuditMatchSummary,
  formatAuditTimestamp,
  resolveAuditCursorPrompt,
  type AuditRunRow,
} from "@/audit/data/fetch-audit-runs";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader, PageShell } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/layout/StatCard";
import { Badge, Button, Card, CardBody, EmptyState, InlineError, Spinner } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, ChevronRight, Copy, RefreshCw } from "lucide-react";
import { useState } from "react";

function CopyPromptButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Button type="button" variant="secondary" size="sm" onClick={() => void copy()}>
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy Cursor prompt"}
    </Button>
  );
}

function AuditRunCard({ run }: { run: AuditRunRow }) {
  const [open, setOpen] = useState(false);
  const prompt = resolveAuditCursorPrompt(run);
  const summary = formatAuditMatchSummary(run);
  const issues = run.mismatched_count + run.missing_count + run.extra_count;

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-5 py-4 text-left"
      >
        <span className="mt-0.5 text-[var(--color-text-muted)]">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={auditStatusTone(run.status)}>{auditStatusLabel(run.status)}</Badge>
            <span className="text-sm font-medium text-[var(--color-text)]">Run #{run.id}</span>
            <span className="text-sm text-[var(--color-text-muted)]">
              {run.system.toUpperCase()} · {run.activity_date}
            </span>
          </div>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {formatAuditTimestamp(run.started_at)}
            {run.finished_at ? ` → ${formatAuditTimestamp(run.finished_at)}` : ""}
            {" · "}
            {summary}
            {issues > 0 ? ` · ${issues} issue(s)` : ""}
            {run.self_correct_attempted ? " · self-correct attempted" : ""}
          </p>
        </div>
      </button>

      {open ? (
        <CardBody className="space-y-4 border-t border-[var(--color-border)] pt-4">
          <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-[var(--color-text-muted)]">Trigger</dt>
              <dd className="font-medium text-[var(--color-text)]">{run.trigger_type}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-text-muted)]">Check type</dt>
              <dd className="font-medium text-[var(--color-text)]">{run.check_type}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-text-muted)]">Rows compared</dt>
              <dd className="font-medium text-[var(--color-text)]">
                {run.expected_rows === 0
                  ? "0 (no rep activity that day)"
                  : `${run.matched}/${run.expected_rows} matched`}
              </dd>
            </div>
            <div>
              <dt className="text-[var(--color-text-muted)]">Mismatched</dt>
              <dd className="font-medium text-[var(--color-text)]">{run.mismatched_count}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-text-muted)]">Missing / extra</dt>
              <dd className="font-medium text-[var(--color-text)]">
                {run.missing_count} / {run.extra_count}
              </dd>
            </div>
          </dl>

          {run.self_correct_sync_run_id ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              Self-correct sync run: #{run.self_correct_sync_run_id}
            </p>
          ) : null}

          {run.error_detail ? (
            <pre className="overflow-x-auto rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 p-3 text-xs text-[var(--color-danger)]">
              {run.error_detail}
            </pre>
          ) : null}

          {run.diffs?.mismatched?.length ? (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-[var(--color-text)]">Mismatched</h3>
              <ul className="space-y-1 text-sm text-[var(--color-text)]">
                {run.diffs.mismatched.slice(0, 15).map((d) => (
                  <li key={`${d.key ?? d.syncoreName}-${d.metric}`}>
                    {d.region} {d.section} {d.syncoreName} {d.metric}: expected {d.expected},
                    warehouse {d.actual}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {prompt ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-[var(--color-text)]">
                  Cursor fix prompt
                </h3>
                <CopyPromptButton text={prompt} />
              </div>
              <pre
                className={cn(
                  "max-h-[32rem] overflow-auto rounded-lg border border-[var(--color-border)]",
                  "bg-[var(--color-surface-muted)] p-3 text-xs leading-relaxed text-[var(--color-text)]",
                )}
              >
                {prompt}
              </pre>
            </div>
          ) : null}
        </CardBody>
      ) : null}
    </Card>
  );
}

export function AuditPage() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["audit-runs"],
    queryFn: () => fetchAuditRuns(),
    refetchInterval: 60_000,
  });

  const runs = data ?? [];
  const latestPass = runs.find((r) => r.status === "pass" || r.status === "corrected");
  const openFailures = runs.filter((r) => r.status === "fail" || r.status === "error").length;

  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === "object" && error !== null && "message" in error
        ? String((error as { message: unknown }).message)
        : error
          ? String(error)
          : null;

  return (
    <AppLayout>
      <PageShell className="max-w-4xl">
        <PageHeader
          title="Data accuracy audit"
          description="Automated weekday checks compare a fresh Syncore scrape to warehouse daily activity (calls, meetings, SDR). Mismatches trigger a self-correct re-sync once; persistent failures get a copy-paste prompt for Cursor."
          actions={
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
              {isFetching ? "Refreshing…" : "Refresh"}
            </Button>
          }
        />

        <div className="grid gap-3 sm:grid-cols-3">
          <StatCard label="Schedule">
            <span className="font-normal">Weekdays 8:00 / 8:15 Toronto</span>
          </StatCard>
          <StatCard label="Open failures">
            <span className="text-2xl font-semibold">{openFailures}</span>
          </StatCard>
          <StatCard label="Last green run">
            <span className="font-normal">
              {latestPass
                ? `#${latestPass.id} · ${latestPass.system.toUpperCase()} · ${latestPass.activity_date}`
                : "—"}
            </span>
          </StatCard>
        </div>

        {isLoading ? <Spinner label="Loading audit runs…" /> : null}

        {errorMessage ? <InlineError message={errorMessage} /> : null}

        {!isLoading && !errorMessage && runs.length === 0 ? (
          <EmptyState
            title="No audit runs yet"
            description="The first scheduled check runs on the next weekday morning after deploy."
          />
        ) : null}

        {!isLoading && !errorMessage && runs.length > 0 ? (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-[var(--color-text)]">Recent runs</h2>
            {runs.map((run) => (
              <AuditRunCard key={run.id} run={run} />
            ))}
          </section>
        ) : null}
      </PageShell>
    </AppLayout>
  );
}
